import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { parse } from 'date-fns';
import { useEffect, type ReactElement } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ModalHistorialSolicitud, { type Form as FormHistorial } from '@/components/ModalHistorialSolicitud';
import PageHeader from '@/components/PageHeader';
import SolicitudesListado from '@/components/SolicitudesListado';
import { type FilterFormData } from '@/components/SolicitudesListado/config';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import { getSolicitudHistorial } from '@/services/solicitudes.service';

import { FORMAT_DATE } from '@/constants/date.constants';
import { getUrlTramite } from '@/helpers/navigation';
import { type ISolicitud } from '@/interfaces/model/solicitudes.interface';

import { ROUTES_LABEL, ROUTES_TRAMITE_RELATIVE } from '../../constants/routes.constants';

const Solicitudes = (): ReactElement => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { triggerFetch } = useApiData({ loading: ['historial'] as const });
	const modalHistorialSolicitud = useModal({});
	const idEstado = searchParams.get('idEstado') ?? undefined;
	const { setBreadcrumb } = usePageContext();
	const { getTramitesContratados, user } = useGlobalContext();

	const methodsHistorial = useForm<FormHistorial>();

	const onView = (rowData: ISolicitud) => {
		const tramite = getTramitesContratados().find(({ tipoTramite }) => tipoTramite.id === rowData.tipoTramite.id);
		if (!tramite) return;
		const pathTramite = getUrlTramite(tramite);
		const path = `${pathTramite}/${ROUTES_TRAMITE_RELATIVE.SOLICITUDES}/${rowData.idTramiteExterno}`;
		setBreadcrumb(undefined);
		navigate(path, { state: { fromSolcitudes: true }});
	};

	const onEdit = (rowData: ISolicitud) => {
		const tramite = getTramitesContratados().find(({ tipoTramite }) => tipoTramite.id === rowData.tipoTramite.id);
		if (!tramite) return;
		const pathTramite = getUrlTramite(tramite);
		const path = `${pathTramite}/${ROUTES_TRAMITE_RELATIVE.BORRADORES}/${rowData.id}`;
		setBreadcrumb(undefined);
		navigate(path, { state: { fromSolcitudes: true }});
	};

	const onHistorial = async (rowData: ISolicitud) => {
		if (!user) return;
		methodsHistorial.reset();

		const { fechaCreacion, id, estadoSolicitud, numeroTramiteExterno, tipoTramite } = rowData;
		const payload = { id, nifEmisor: user.nifEmisor, idTipoSector: user.tipoSectorEmisor.id };

		const { data } = await triggerFetch({
			fetch: getSolicitudHistorial,
			payload,
			name: 'historial',
			error: 'Se ha producido un error al recuperar el historial.',
		});

		if (data) {
			const dataForm: FormHistorial = {
				numeroTramite: numeroTramiteExterno,
				estadoTramite: estadoSolicitud,
				tipoNNJJ: tipoTramite,
				fechaCreacion: fechaCreacion ? parse(fechaCreacion, FORMAT_DATE.WITH_HOUR, new Date()) : undefined,
				historial: data,
			};
			methodsHistorial.reset(dataForm);
			modalHistorialSolicitud.openModal();
		}
	};

	const makeDefaultFilter = (values: { idEstado?: string } = {}): Partial<FilterFormData> | undefined => {
		const defaultFilter: Partial<FilterFormData> = {};
		const { idEstado } = values;

		if (idEstado) {
			defaultFilter.listaIdsEstados = [idEstado];
		}

		return defaultFilter;
	};

	useEffect(() => {
		setBreadcrumb([{ label: ROUTES_LABEL.SOLICITUDES, url: location.pathname }]);
	}, []);

	return (
		<>
			<PageHeader hasHelp title="Solicitudes" className="u-mt-8" />
			<SolicitudesListado
				defaultFilter={makeDefaultFilter({ idEstado })}
				onView={onView}
				onEdit={onEdit}
				onHistorial={onHistorial}
			/>
			<FormProvider {...methodsHistorial}>
				<ModalHistorialSolicitud isOpen={modalHistorialSolicitud.isShowing} onClose={modalHistorialSolicitud.closeModal} />
			</FormProvider>
		</>
	);
};

export default Solicitudes;
